import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  IonPage, IonContent, IonHeader, IonToolbar, IonButtons, IonTitle, IonButton, IonIcon, IonSpinner,
  IonRadio, IonCheckbox, IonToggle,
} from '@ionic/react';
import {
  closeOutline, documentTextOutline, pricetagOutline, calendarOutline,
  chevronDownOutline, chevronUpOutline, infiniteOutline, informationCircleOutline,
} from 'ionicons/icons';
import type { HelcimPaymentPlan, CreatePaymentPlanPayload } from '@rsmaids/shared/services/api/helcim/helcim';
import { createPaymentPlan, updatePaymentPlan } from '../../../services/helcimInvoicingService';
import styles from './PlanFormDesktop.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';

// ─── option constants (ported verbatim from src/components/HelcimPlansModal.tsx) ──
interface DropdownOption {
  label: string;
  value: string;
}

function optLabel(opts: DropdownOption[], value: string): string {
  return opts.find(o => o.value === value)?.label ?? value;
}

const TRIAL_UNIT_OPTIONS: DropdownOption[] = [
  { value: 'days', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.day2'); } },
  { value: 'weeks', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.weeks'); } },
];

const PERIOD_OPTIONS: DropdownOption[] = [
  { value: 'daily', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.daily'); } },
  { value: 'weekly', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.weekly'); } },
  { value: 'monthly', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.monthly'); } },
  { value: 'yearly', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.yearly'); } },
  { value: 'custom', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.custom'); } },
];

const CUSTOM_PERIOD_OPTIONS: DropdownOption[] = [
  { value: 'daily', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.dayS'); } },
  { value: 'weekly', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.weekS'); } },
  { value: 'monthly', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.month2'); } },
  { value: 'yearly', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.yearS'); } },
];

const CHARGE_OCCURS_OPTIONS: DropdownOption[] = [
  { value: 'first_payment', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.withTheFirstPayment'); } },
  { value: 'signup', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.onSignUp'); } },
];

const WEEKDAY_OPTIONS: DropdownOption[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  .map(d => ({ value: d, label: d }));

const MONTH_OPTIONS: DropdownOption[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
  .map((m, i) => ({ value: m, label: new Date(2000, i).toLocaleString('default', { month: 'long' }) }));

const MONTH_DAY_OPTIONS: DropdownOption[] = Array.from({ length: 28 }, (_, i) => String(i + 1).padStart(2, '0'))
  .map(d => ({ value: d, label: String(parseInt(d, 10)) }));

const TAX_CALC_OPTIONS: DropdownOption[] = [
  { value: 'full', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.fullTax'); } },
  { value: 'state', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.stateProvinceOnly'); } },
  { value: 'country', get label() { return i18n.t('desktop.pages.billing.PlanFormDesktop.countryOnly'); } },
];

// ─── desktop custom-dropdown positioning (same mechanism as
// AddSubscriberModalDesktop.tsx's useFieldDropdown — trigger ref + portal-to-
// body list ref, position computed from the trigger's rect on open, closed on
// outside mousedown). Reused as-is for the day-grid popover too — it just
// portals different content (a grid instead of an option list). ──────────────
interface DropdownPos { top: number; left: number; width: number }

// triggerRef is returned directly (assigned to a real DOM ref= at each call
// site, in the same component scope useFieldDropdown() is called from — safe).
// listRef never leaves this hook's closure at all: it's only ever needed by
// the outside-click handler below and by renderList/renderPortal, both
// defined here, so there's no ref/prop boundary for eslint-plugin-react-hooks'
// `refs` rule to (incorrectly) flag in the first place.
function useFieldDropdown() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<DropdownPos>({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(v => !v);
  };

  // Renders a plain option-button list, portaled to document.body. Called as
  // a plain function (`dd.renderList(...)`), never as a JSX <Component/> —
  // that's what keeps listRef from ever crossing a component-prop boundary.
  const renderList = (options: DropdownOption[], value: string, onSelect: (value: string) => void) => {
    if (!open) return null;
    return ReactDOM.createPortal(
      <div ref={listRef} className={styles.customDropdownList} style={{ top: pos.top, left: pos.left, width: pos.width }}>
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            className={`${styles.customDropdownItem} ${opt.value === value ? styles.customDropdownItemActive : ''}`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => { onSelect(opt.value); setOpen(false); }}
          >
            {opt.label}
          </button>
        ))}
      </div>,
      document.body,
    );
  };

  // Same idea for dropdowns whose content isn't a plain option list (the
  // day-grid picker).
  const renderPortal = (className: string, children: React.ReactNode) => {
    if (!open) return null;
    return ReactDOM.createPortal(
      <div ref={listRef} className={className} style={{ top: pos.top, left: pos.left }}>
        {children}
      </div>,
      document.body,
    );
  };

  return [{ open, setOpen, pos, toggle, renderList, renderPortal }, triggerRef] as const;
}

// ─── component ────────────────────────────────────────────────────────────────

interface PlanFormDesktopProps {
  plan?: HelcimPaymentPlan | null;
  onClose: () => void;
  onSaved: () => void;
}

const PlanFormDesktop: React.FC<PlanFormDesktopProps> = ({ plan, onClose, onSaved }) => {
  const { t: tr } = useTranslation();
  const isEditMode = !!plan;

  // ── Basic Info ──────────────────────────────────────────────────────────────
  const [formName, setFormName] = useState(plan?.name ?? '');
  const [formDesc, setFormDesc] = useState(plan?.description ?? '');
  const trialRaw = Number(plan?.freeTrialPeriod ?? 0);
  const trialInWeeks = trialRaw > 0 && trialRaw % 7 === 0;
  const [formTrialPeriod, setFormTrialPeriod] = useState(trialRaw > 0);
  const [formTrialAmount, setFormTrialAmount] = useState(
    trialInWeeks ? String(trialRaw / 7) : (trialRaw > 0 ? String(trialRaw) : '1')
  );
  const [formTrialUnit, setFormTrialUnit] = useState<'days' | 'weeks'>(trialInWeeks ? 'weeks' : 'days');
  const [origTrial] = useState<{ period: boolean; amount: string; unit: 'days' | 'weeks' } | null>(
    () => plan ? { period: trialRaw > 0, amount: trialInWeeks ? String(trialRaw / 7) : (trialRaw > 0 ? String(trialRaw) : '1'), unit: trialInWeeks ? 'weeks' : 'days' } : null
  );

  // ── Pricing ──────────────────────────────────────────────────────────────────
  const [formAmount, setFormAmount] = useState(plan?.recurringAmount != null ? String(plan.recurringAmount) : '');
  const [formPeriod, setFormPeriod] = useState((plan?.billingPeriod ?? 'monthly').toLowerCase());
  const [formIncrements, setFormIncrements] = useState(String(plan?.billingPeriodIncrements ?? 1));
  const [formSetupFee, setFormSetupFee] = useState(plan?.setupAmount != null ? String(plan.setupAmount) : '');
  const [formChargeOccurs, setFormChargeOccurs] = useState(
    (plan?.billSetupImmediately ?? 'first_billing') === 'immediate' ? 'signup' : 'first_payment'
  );

  // ── Billing Schedule ─────────────────────────────────────────────────────────
  const initDbDay = plan?.dateBilling ?? '';
  const [formBillingDay, setFormBillingDay] = useState<'signup' | 'pick'>(
    initDbDay && initDbDay !== 'Sign-up' && initDbDay !== 'Daily' ? 'pick' : 'signup'
  );
  const [formPickDayNum, setFormPickDayNum] = useState(initDbDay && /^\d+$/.test(initDbDay) ? String(parseInt(initDbDay, 10)) : '1');
  const [formPickWeekday, setFormPickWeekday] = useState(
    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(initDbDay) ? initDbDay : 'Monday'
  );
  const [formPickMonth, setFormPickMonth] = useState(initDbDay.includes('-') ? (initDbDay.split('-')[0] ?? '01') : '01');
  const [formPickMonthDay, setFormPickMonthDay] = useState(initDbDay.includes('-') ? (initDbDay.split('-')[1] ?? '01') : '01');
  const [formIsProrated, setFormIsProrated] = useState(plan?.isProrated !== 'no');
  const initTermType = plan?.termType as string | undefined;
  const [formTermType, setFormTermType] = useState<'forever' | 'expires'>(initTermType === 'expires' ? 'expires' : 'forever');
  const [formTermLength, setFormTermLength] = useState(String((plan?.termLength as number | undefined) ?? 1));

  // ── More Options ─────────────────────────────────────────────────────────────
  const initTaxType = plan?.taxType as string | undefined;
  const [formTaxType, setFormTaxType] = useState<'no_tax' | 'customer' | 'merchant'>(
    initTaxType === 'customer' ? 'customer' : initTaxType === 'merchant' ? 'merchant' : 'no_tax'
  );
  const initTaxCalc = plan?.taxCalculation as string | undefined;
  const [formTaxCalc, setFormTaxCalc] = useState(initTaxCalc === 'province_only' ? 'state' : initTaxCalc === 'country_only' ? 'country' : 'full');
  const initPayMethod = plan?.paymentMethod as string | undefined;
  const [formPayCard, setFormPayCard] = useState(plan ? initPayMethod !== 'bank' : true);
  const [formPayACH, setFormPayACH] = useState(plan ? initPayMethod !== 'card' : true);
  const [formNotifRecurring, setFormNotifRecurring] = useState(!!plan?.businessEmail);
  const [formBusinessEmail, setFormBusinessEmail] = useState(plan?.businessEmail ?? '');
  const [formMoreOpen, setFormMoreOpen] = useState(false);

  // ── Save / error state ───────────────────────────────────────────────────────
  const [formSaving, setFormSaving] = useState(false);
  const [error, setError] = useState('');
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingChoice, setPricingChoice] = useState<'future' | 'all'>('future');

  // ── Dropdowns ─────────────────────────────────────────────────────────────────
  const [trialUnitDd, trialUnitTriggerRef] = useFieldDropdown();
  const [periodDd, periodTriggerRef] = useFieldDropdown();
  const [customPeriodDd, customPeriodTriggerRef] = useFieldDropdown();
  const [chargeOccursDd, chargeOccursTriggerRef] = useFieldDropdown();
  const [weekdayDd, weekdayTriggerRef] = useFieldDropdown();
  const [monthDd, monthTriggerRef] = useFieldDropdown();
  const [monthDayDd, monthDayTriggerRef] = useFieldDropdown();
  const [taxCalcDd, taxCalcTriggerRef] = useFieldDropdown();
  const [dayGridDd, dayGridTriggerRef] = useFieldDropdown();

  // ── Standalone <ion-radio> refs (no IonRadioGroup) — checked state set
  // imperatively via ref, same pattern as HelcimPlansModal.tsx /
  // AddSubscriberModalDesktop.tsx (ion-radio's TS type omits `checked` even
  // though the underlying custom element genuinely has it). ────────────────────
  const signupRadioRef = useRef<HTMLIonRadioElement>(null);
  const pickDayRadioRef = useRef<HTMLIonRadioElement>(null);
  const foreverRadioRef = useRef<HTMLIonRadioElement>(null);
  const expiresRadioRef = useRef<HTMLIonRadioElement>(null);
  const taxNoRadioRef = useRef<HTMLIonRadioElement>(null);
  const taxCustomerRadioRef = useRef<HTMLIonRadioElement>(null);
  const taxMerchantRadioRef = useRef<HTMLIonRadioElement>(null);
  const pricingFutureRadioRef = useRef<HTMLIonRadioElement>(null);
  const pricingAllRadioRef = useRef<HTMLIonRadioElement>(null);

  useEffect(() => {
    if (signupRadioRef.current) (signupRadioRef.current as unknown as { checked: boolean }).checked = formBillingDay === 'signup';
    if (pickDayRadioRef.current) (pickDayRadioRef.current as unknown as { checked: boolean }).checked = formBillingDay === 'pick';
  }, [formBillingDay]);

  useEffect(() => {
    if (foreverRadioRef.current) (foreverRadioRef.current as unknown as { checked: boolean }).checked = formTermType === 'forever';
    if (expiresRadioRef.current) (expiresRadioRef.current as unknown as { checked: boolean }).checked = formTermType === 'expires';
  }, [formTermType]);

  useEffect(() => {
    if (taxNoRadioRef.current) (taxNoRadioRef.current as unknown as { checked: boolean }).checked = formTaxType === 'no_tax';
    if (taxCustomerRadioRef.current) (taxCustomerRadioRef.current as unknown as { checked: boolean }).checked = formTaxType === 'customer';
    if (taxMerchantRadioRef.current) (taxMerchantRadioRef.current as unknown as { checked: boolean }).checked = formTaxType === 'merchant';
  }, [formTaxType]);

  useEffect(() => {
    if (pricingFutureRadioRef.current) (pricingFutureRadioRef.current as unknown as { checked: boolean }).checked = pricingChoice === 'future';
    if (pricingAllRadioRef.current) (pricingAllRadioRef.current as unknown as { checked: boolean }).checked = pricingChoice === 'all';
  }, [pricingChoice]);

  // ── Trial-changed note (edit mode only) ──────────────────────────────────────
  const trialChanged = isEditMode && origTrial !== null && (
    formTrialPeriod !== origTrial.period ||
    formTrialAmount !== origTrial.amount ||
    formTrialUnit !== origTrial.unit
  );

  // ── Save (ported verbatim from HelcimPlansModal.tsx's handleSaveForm) ────────
  const handleSaveForm = async (updateExisting?: boolean) => {
    if (!formName.trim()) return;
    if (isEditMode && updateExisting === undefined) {
      const origAmount = plan?.recurringAmount;
      if (origAmount !== undefined && parseFloat(formAmount) !== origAmount) {
        setPricingChoice('future');
        setPricingDialogOpen(true);
        return;
      }
    }
    setFormSaving(true);
    setError('');
    try {
      const payload: CreatePaymentPlanPayload = {
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        type: (formBillingDay === 'pick' ? 'cycle' : 'subscription') as 'subscription' | 'cycle',
        ...(formBillingDay === 'pick' ? { isProrated: formIsProrated ? 'yes' : 'no' } : {}),
        billingPeriod: formPeriod as CreatePaymentPlanPayload['billingPeriod'],
        billingPeriodIncrements: parseInt(formIncrements) || 1,
        recurringAmount: parseFloat(formAmount) || 0,
        currency: 'USD' as const,
        ...(formSetupFee.trim() ? { setupAmount: parseFloat(formSetupFee) || 0 } : {}),
        dateBilling: (() => {
          if (formBillingDay === 'signup') return 'Sign-up';
          const period = formPeriod.toLowerCase();
          if (period === 'daily') return 'Daily';
          if (period === 'weekly') return formPickWeekday;
          if (period === 'yearly') return `${formPickMonth}-${formPickMonthDay}`;
          // monthly: DD format
          return formPickDayNum === 'last' ? 'Last' : String(parseInt(formPickDayNum) || 1).padStart(2, '0');
        })(),
        termType: formTermType,
        ...(formTermType === 'expires' ? { termLength: parseInt(formTermLength) || 1 } : {}),
        ...(formSetupFee.trim() && parseFloat(formSetupFee) > 0 ? { billSetupImmediately: formChargeOccurs === 'signup' ? 'immediate' as const : 'first_billing' as const } : {}),
        freeTrialPeriod: formTrialPeriod ? (parseInt(formTrialAmount) || 1) * (formTrialUnit === 'weeks' ? 7 : 1) : 0,
        taxType: formTaxType as CreatePaymentPlanPayload['taxType'],
        ...(formTaxType === 'merchant' ? { taxCalculation: formTaxCalc === 'state' ? 'province_only' : formTaxCalc === 'country' ? 'country_only' : 'country_province' as CreatePaymentPlanPayload['taxCalculation'] } : {}),
        paymentMethod: formPayCard && formPayACH ? 'card_bank' : formPayCard ? 'card' : formPayACH ? 'bank' : undefined,
        emailSettingRecurringQueue: formNotifRecurring ? 'merchant' : 'off',
        businessEmail: formNotifRecurring ? formBusinessEmail.trim() : '',
      };
      if (!isEditMode) {
        await createPaymentPlan(payload);
      } else if (plan?.id) {
        await updatePaymentPlan(plan.id, {
          ...payload,
          ...(updateExisting !== undefined ? { updateExistingSubscriptions: updateExisting } : {}),
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setFormSaving(false);
    }
  };

  const canSubmit = !formSaving && !!formName.trim();

  return (
    <IonPage className={styles.page}>
      <IonHeader className={styles.header}>
        <IonToolbar className={styles.toolbar}>
          <IonTitle slot="start" className={styles.pageTitle}>{isEditMode ? tr('desktop.pages.billing.PlanFormDesktop.editPlan') : tr('desktop.pages.billing.PlanFormDesktop.createANewPlan')}</IonTitle>
          <IonButtons slot="end">
            {error && <span className={styles.headerError}>{error}</span>}
            <IonButton
              fill="solid"
              size="small"
              className={styles.primaryBtn}
              disabled={!canSubmit}
              onClick={() => handleSaveForm()}
            >
              {formSaving ? <IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16 }} /> : (isEditMode ? tr('desktop.pages.billing.PlanFormDesktop.save') : tr('desktop.pages.billing.PlanFormDesktop.createPlan'))}
            </IonButton>
            <IonButton fill="clear" shape="round" mode="md" className={styles.closeBtn} onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className={styles.content}>
        <div className={styles.formBody}>

          {/* ── Left Column ──────────────────────────────────────────────── */}
          <div className={styles.formColLeft}>

            {/* Basic Info */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={documentTextOutline} />{tr('desktop.pages.billing.PlanFormDesktop.basicInfo')}
              </div>

              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.planName')}</label>
                  <div className={styles.fieldBox}>
                    <input
                      className={styles.fieldInput}
                      type="text"
                      placeholder={tr('desktop.pages.billing.PlanFormDesktop.eGWeeklyRecurringCleaning')}
                      value={formName}
                      onChange={e => setFormName(e.target.value)}
                    />
                  </div>
                </div>

                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.description')}</label>
                  <textarea
                    className={styles.textarea}
                    placeholder={tr('desktop.pages.billing.PlanFormDesktop.optionalDescription')}
                    value={formDesc}
                    onChange={e => setFormDesc(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className={`${styles.formGroupFull}`}>
                  <div className={styles.checkRow} onClick={() => setFormTrialPeriod(v => !v)}>
                    <IonCheckbox checked={formTrialPeriod} className={styles.checkbox} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.thisPlanHasATrialPeriod')}</span>
                  </div>
                </div>

                {formTrialPeriod && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.trialDuration')}</label>
                      <div className={styles.fieldBox}>
                        <input
                          className={styles.fieldInput}
                          type="number"
                          min="1"
                          value={formTrialAmount}
                          onChange={e => setFormTrialAmount(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.unit')}</label>
                      <div className={styles.customDropdown}>
                        <div
                          ref={trialUnitTriggerRef}
                          role="button"
                          tabIndex={0}
                          className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                          onClick={trialUnitDd.toggle}
                          onKeyDown={e => e.key === 'Enter' && trialUnitDd.toggle()}
                        >
                          <span className={styles.fieldInput}>{optLabel(TRIAL_UNIT_OPTIONS, formTrialUnit)}</span>
                          <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${trialUnitDd.open ? styles.dropdownCaretOpen : ''}`} />
                        </div>
                        {trialUnitDd.renderList(TRIAL_UNIT_OPTIONS, formTrialUnit, v => setFormTrialUnit(v as 'days' | 'weeks'))}
                      </div>
                    </div>
                  </>
                )}

                {trialChanged && (
                  <div className={`${styles.infoRow} ${styles.formGroupFull}`}>
                    <IonIcon icon={informationCircleOutline} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.theChangeWillApplyForNew')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={pricetagOutline} />{tr('desktop.pages.billing.PlanFormDesktop.pricing')}
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.amount')}</label>
                  <div className={styles.amountBox}>
                    <span className={styles.amountPrefix}>$</span>
                    <input
                      className={styles.amountInput}
                      type="number"
                      min="0"
                      step="0.01"
                      value={formAmount}
                      onChange={e => setFormAmount(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.billingPeriod')}</label>
                  <div className={styles.customDropdown}>
                    <div
                      ref={periodTriggerRef}
                      role="button"
                      tabIndex={isEditMode ? -1 : 0}
                      className={`${styles.fieldBox} ${styles.customDropdownTrigger} ${isEditMode ? styles.disabled : ''}`}
                      onClick={() => !isEditMode && periodDd.toggle()}
                      onKeyDown={e => !isEditMode && e.key === 'Enter' && periodDd.toggle()}
                    >
                      <span className={styles.fieldInput}>{optLabel(PERIOD_OPTIONS, formIncrements !== '1' ? 'custom' : formPeriod)}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${periodDd.open ? styles.dropdownCaretOpen : ''}`} />
                    </div>
                    {periodDd.renderList(PERIOD_OPTIONS, formIncrements !== '1' ? 'custom' : formPeriod, v => {
                      if (v === 'custom') { setFormIncrements('2'); } else { setFormPeriod(v); setFormIncrements('1'); setFormBillingDay('signup'); }
                    })}
                  </div>
                </div>

                {formIncrements !== '1' && (
                  <>
                    <div className={`${styles.formGroup} ${isEditMode ? styles.disabled : ''}`}>
                      <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.every')}</label>
                      <div className={styles.fieldBox}>
                        <input
                          className={styles.fieldInput}
                          type="number"
                          min="2"
                          disabled={isEditMode}
                          value={formIncrements}
                          onChange={e => setFormIncrements(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className={`${styles.formGroup} ${isEditMode ? styles.disabled : ''}`}>
                      <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.customBillingPeriod')}</label>
                      <div className={styles.customDropdown}>
                        <div
                          ref={customPeriodTriggerRef}
                          role="button"
                          tabIndex={isEditMode ? -1 : 0}
                          className={`${styles.fieldBox} ${styles.customDropdownTrigger} ${isEditMode ? styles.disabled : ''}`}
                          onClick={() => !isEditMode && customPeriodDd.toggle()}
                          onKeyDown={e => !isEditMode && e.key === 'Enter' && customPeriodDd.toggle()}
                        >
                          <span className={styles.fieldInput}>{optLabel(CUSTOM_PERIOD_OPTIONS, formPeriod)}</span>
                          <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${customPeriodDd.open ? styles.dropdownCaretOpen : ''}`} />
                        </div>
                        {customPeriodDd.renderList(CUSTOM_PERIOD_OPTIONS, formPeriod, v => setFormPeriod(v))}
                      </div>
                    </div>
                  </>
                )}

                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.setUpFee')} <span className={styles.optionalTag}>{tr('desktop.pages.billing.PlanFormDesktop.optional')}</span></label>
                </div>
                <div className={styles.formGroup}>
                  <div className={styles.amountBox}>
                    <span className={styles.amountPrefix}>$</span>
                    <input
                      className={styles.amountInput}
                      type="number"
                      min="0"
                      step="0.01"
                      value={formSetupFee}
                      onChange={e => setFormSetupFee(e.target.value)}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <div className={styles.customDropdown}>
                    <div
                      ref={chargeOccursTriggerRef}
                      role="button"
                      tabIndex={0}
                      className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                      onClick={chargeOccursDd.toggle}
                      onKeyDown={e => e.key === 'Enter' && chargeOccursDd.toggle()}
                    >
                      <span className={styles.fieldInput}>{optLabel(CHARGE_OCCURS_OPTIONS, formChargeOccurs)}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${chargeOccursDd.open ? styles.dropdownCaretOpen : ''}`} />
                    </div>
                    {chargeOccursDd.renderList(CHARGE_OCCURS_OPTIONS, formChargeOccurs, v => setFormChargeOccurs(v))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right Column ─────────────────────────────────────────────── */}
          <div className={styles.formColRight}>

            {/* Billing Schedule */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={calendarOutline} />{tr('desktop.pages.billing.PlanFormDesktop.billingSchedule')}
              </div>

              {isEditMode ? (
                <>
                  <div className={styles.readOnlyRow}>
                    <div className={styles.readOnlyItem}>
                      <span className={styles.readOnlyLabel}>{tr('desktop.pages.billing.PlanFormDesktop.billingDay')}</span>
                      <div className={styles.readOnlyValueRow}>
                        <IonIcon icon={calendarOutline} />
                        <div>
                          <p className={styles.readOnlyTitle}>{formBillingDay === 'signup' ? tr('desktop.pages.billing.PlanFormDesktop.onSignUp') : tr('desktop.pages.billing.PlanFormDesktop.pickADay')}</p>
                          <p className={styles.readOnlySub}>{formBillingDay === 'signup' ? tr('desktop.pages.billing.PlanFormDesktop.billedOnDayOfActivation') : tr('desktop.pages.billing.PlanFormDesktop.billedOnASpecificDay')}</p>
                        </div>
                      </div>
                    </div>
                    <div className={styles.readOnlyItem}>
                      <span className={styles.readOnlyLabel}>{tr('desktop.pages.billing.PlanFormDesktop.endOfTerm')}</span>
                      <div className={styles.readOnlyValueRow}>
                        <IonIcon icon={infiniteOutline} />
                        <div>
                          <p className={styles.readOnlyTitle}>{formTermType === 'forever' ? tr('desktop.pages.billing.PlanFormDesktop.forever') : tr('desktop.pages.billing.PlanFormDesktop.expire')}</p>
                          <p className={styles.readOnlySub}>
                            {formTermType === 'forever'
                              ? tr('desktop.pages.billing.PlanFormDesktop.thePlanContinuesUntilCancelled')
                              : tr('desktop.pages.billing.PlanFormDesktop.planEndsAfterBillingCycle', { formTermLength, v2: formTermLength === '1' ? '' : 's' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {formBillingDay === 'pick' && (
                    <div className={styles.toggleRow}>
                      <IonToggle checked={formIsProrated} onIonChange={e => setFormIsProrated(e.detail.checked)} />
                      <span className={styles.toggleLabel}>{tr('desktop.pages.billing.PlanFormDesktop.prorateBilling')}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className={styles.subHeading}>{tr('desktop.pages.billing.PlanFormDesktop.billingDay')}</p>
                  <p className={styles.subSubtitle}>{tr('desktop.pages.billing.PlanFormDesktop.chooseWhenYourCustomersWillBe')}</p>
                  <div className={styles.choiceCardRow}>
                    <button
                      type="button"
                      className={`${styles.choiceCard} ${formBillingDay === 'signup' ? styles.choiceCardActive : ''}`}
                      onClick={() => setFormBillingDay('signup')}
                    >
                      <IonRadio ref={signupRadioRef} value="signup" className={styles.radioCircle} />
                      <div className={styles.choiceCardText}>
                        <p className={styles.choiceCardTitle}>{tr('desktop.pages.billing.PlanFormDesktop.onSignUp')}</p>
                        <p className={styles.choiceCardSub}>{tr('desktop.pages.billing.PlanFormDesktop.billedOnDayOfActivation')}</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      disabled={formPeriod === 'daily'}
                      className={`${styles.choiceCard} ${formBillingDay === 'pick' ? styles.choiceCardActive : ''}`}
                      onClick={() => formPeriod !== 'daily' && setFormBillingDay('pick')}
                    >
                      <IonRadio ref={pickDayRadioRef} value="pick" className={styles.radioCircle} />
                      <div className={styles.choiceCardText}>
                        <p className={styles.choiceCardTitle}>{tr('desktop.pages.billing.PlanFormDesktop.pickADay')}</p>
                        <p className={styles.choiceCardSub}>{tr('desktop.pages.billing.PlanFormDesktop.billedOnASpecificDay')}</p>
                      </div>
                    </button>
                  </div>

                  {formBillingDay === 'pick' && formPeriod === 'monthly' && (
                    <div className={styles.formGroup} style={{ marginBottom: 10 }}>
                      <div className={styles.customDropdown}>
                        <div
                          ref={dayGridTriggerRef}
                          role="button"
                          tabIndex={0}
                          className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                          onClick={dayGridDd.toggle}
                          onKeyDown={e => e.key === 'Enter' && dayGridDd.toggle()}
                        >
                          <span className={styles.fieldInput}>{formPickDayNum === 'last' ? tr('desktop.pages.billing.PlanFormDesktop.lastDay') : tr('desktop.pages.billing.PlanFormDesktop.day3', { formPickDayNum })}</span>
                          <IonIcon icon={calendarOutline} className={styles.dropdownCaret} />
                        </div>
                        {dayGridDd.renderPortal(styles.dayGridPanel, (
                          <>
                            <p className={styles.dayGridTitle}>{tr('desktop.pages.billing.PlanFormDesktop.selectBillingDay')}</p>
                            <div className={styles.dayGridCells}>
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                <button
                                  key={d}
                                  type="button"
                                  className={`${styles.dayCell} ${formPickDayNum === String(d) ? styles.dayCellActive : ''}`}
                                  onClick={() => { setFormPickDayNum(String(d)); dayGridDd.setOpen(false); }}
                                >{d}</button>
                              ))}
                              <button
                                type="button"
                                className={`${styles.dayCell} ${styles.dayCellLast} ${formPickDayNum === 'last' ? styles.dayCellActive : ''}`}
                                onClick={() => { setFormPickDayNum('last'); dayGridDd.setOpen(false); }}
                              >{tr('desktop.pages.billing.PlanFormDesktop.lastDay')}</button>
                            </div>
                          </>
                        ))}
                      </div>
                    </div>
                  )}

                  {formBillingDay === 'pick' && formPeriod === 'weekly' && (
                    <div className={styles.formGroup} style={{ marginBottom: 10 }}>
                      <div className={styles.customDropdown}>
                        <div
                          ref={weekdayTriggerRef}
                          role="button"
                          tabIndex={0}
                          className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                          onClick={weekdayDd.toggle}
                          onKeyDown={e => e.key === 'Enter' && weekdayDd.toggle()}
                        >
                          <span className={styles.fieldInput}>{formPickWeekday}</span>
                          <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${weekdayDd.open ? styles.dropdownCaretOpen : ''}`} />
                        </div>
                        {weekdayDd.renderList(WEEKDAY_OPTIONS, formPickWeekday, v => setFormPickWeekday(v))}
                      </div>
                    </div>
                  )}

                  {formBillingDay === 'pick' && formPeriod === 'yearly' && (
                    <div className={`${styles.formGrid}`} style={{ marginBottom: 10 }}>
                      <div className={styles.formGroup}>
                        <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.month')}</label>
                        <div className={styles.customDropdown}>
                          <div
                            ref={monthTriggerRef}
                            role="button"
                            tabIndex={0}
                            className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                            onClick={monthDd.toggle}
                            onKeyDown={e => e.key === 'Enter' && monthDd.toggle()}
                          >
                            <span className={styles.fieldInput}>{optLabel(MONTH_OPTIONS, formPickMonth)}</span>
                            <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${monthDd.open ? styles.dropdownCaretOpen : ''}`} />
                          </div>
                          {monthDd.renderList(MONTH_OPTIONS, formPickMonth, v => setFormPickMonth(v))}
                        </div>
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.day')}</label>
                        <div className={styles.customDropdown}>
                          <div
                            ref={monthDayTriggerRef}
                            role="button"
                            tabIndex={0}
                            className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                            onClick={monthDayDd.toggle}
                            onKeyDown={e => e.key === 'Enter' && monthDayDd.toggle()}
                          >
                            <span className={styles.fieldInput}>{optLabel(MONTH_DAY_OPTIONS, formPickMonthDay)}</span>
                            <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${monthDayDd.open ? styles.dropdownCaretOpen : ''}`} />
                          </div>
                          {monthDayDd.renderList(MONTH_DAY_OPTIONS, formPickMonthDay, v => setFormPickMonthDay(v))}
                        </div>
                      </div>
                    </div>
                  )}

                  {formBillingDay === 'pick' && (
                    <div className={styles.toggleRow}>
                      <IonToggle checked={formIsProrated} onIonChange={e => setFormIsProrated(e.detail.checked)} />
                      <span className={styles.toggleLabel}>{tr('desktop.pages.billing.PlanFormDesktop.prorateBilling')}</span>
                    </div>
                  )}

                  <div className={styles.divider} />
                  <p className={styles.subHeading}>{tr('desktop.pages.billing.PlanFormDesktop.endOfTerm')}</p>
                  <p className={styles.subSubtitle}>{tr('desktop.pages.billing.PlanFormDesktop.chooseWhenYourCustomersWillBe')}</p>
                  <div className={styles.choiceCardRow}>
                    <button
                      type="button"
                      className={`${styles.choiceCard} ${formTermType === 'forever' ? styles.choiceCardActive : ''}`}
                      onClick={() => setFormTermType('forever')}
                    >
                      <IonRadio ref={foreverRadioRef} value="forever" className={styles.radioCircle} />
                      <div className={styles.choiceCardText}>
                        <p className={styles.choiceCardTitle}>{tr('desktop.pages.billing.PlanFormDesktop.forever')}</p>
                        <p className={styles.choiceCardSub}>{tr('desktop.pages.billing.PlanFormDesktop.thePlanContinuesUntilCancelled')}</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`${styles.choiceCard} ${formTermType === 'expires' ? styles.choiceCardActive : ''}`}
                      onClick={() => setFormTermType('expires')}
                    >
                      <IonRadio ref={expiresRadioRef} value="expires" className={styles.radioCircle} />
                      <div className={styles.choiceCardText}>
                        <p className={styles.choiceCardTitle}>{tr('desktop.pages.billing.PlanFormDesktop.expire')}</p>
                        <p className={styles.choiceCardSub}>{tr('desktop.pages.billing.PlanFormDesktop.planEndsAfterSetOfCycles')}</p>
                      </div>
                    </button>
                  </div>

                  {formTermType === 'expires' && (
                    <div className={styles.formGroup}>
                      <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.billingCycles')}</label>
                      <div className={styles.stepperInputWrap}>
                        <input
                          className={styles.stepperInput}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formTermLength}
                          onChange={e => setFormTermLength(String(Math.max(1, parseInt(e.target.value) || 1)))}
                        />
                        <span className={styles.stepperLabel}>{tr('desktop.pages.billing.PlanFormDesktop.cycle')}{formTermLength !== '1' ? 's' : ''}</span>
                        <div className={styles.spinBtns}>
                          <button type="button" className={styles.spinUp} onClick={() => setFormTermLength(v => String((parseInt(v) || 1) + 1))}>
                            <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 5L5 1L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <button type="button" className={styles.spinDown} onClick={() => setFormTermLength(v => String(Math.max(1, (parseInt(v) || 1) - 1)))}>
                            <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* More Options */}
            <div className={styles.card}>
              <button type="button" className={`${styles.cardTitle} ${styles.cardTitleCollapsible}`} onClick={() => setFormMoreOpen(v => !v)} style={{ marginBottom: formMoreOpen ? 14 : 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{tr('desktop.pages.billing.PlanFormDesktop.moreOptions')}</span>
                <IonIcon icon={formMoreOpen ? chevronUpOutline : chevronDownOutline} className={styles.cardChevron} />
              </button>

              {formMoreOpen && (
                <>
                  <p className={styles.subHeading}>{tr('desktop.pages.billing.PlanFormDesktop.paymentMethod')}</p>
                  <p className={styles.subSubtitle}>{tr('desktop.pages.billing.PlanFormDesktop.selectAtLeastOnePaymentMethod')}</p>
                  <div className={styles.checkRow} onClick={() => setFormPayCard(v => !v)}>
                    <IonCheckbox checked={formPayCard} className={styles.checkbox} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.creditCard')}</span>
                  </div>
                  <div className={styles.checkRow} onClick={() => setFormPayACH(v => !v)}>
                    <IonCheckbox checked={formPayACH} className={styles.checkbox} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.achPayment')}</span>
                  </div>

                  <div className={styles.divider} />
                  <p className={styles.subHeading}>{tr('desktop.pages.billing.PlanFormDesktop.taxSetting')}</p>
                  <p className={styles.subSubtitle}>{tr('desktop.pages.billing.PlanFormDesktop.chooseHowThisPlanIsTaxed')}</p>
                  <div className={styles.radioRow} onClick={() => setFormTaxType('no_tax')}>
                    <IonRadio ref={taxNoRadioRef} value="no_tax" className={styles.radioCircle} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.noTax')}</span>
                  </div>
                  <div className={styles.radioRow} onClick={() => setFormTaxType('customer')}>
                    <IonRadio ref={taxCustomerRadioRef} value="customer" className={styles.radioCircle} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.basedOnCustomerSLocation')}</span>
                  </div>
                  <div className={styles.radioRow} onClick={() => setFormTaxType('merchant')}>
                    <IonRadio ref={taxMerchantRadioRef} value="merchant" className={styles.radioCircle} />
                    <span>{tr('desktop.pages.billing.PlanFormDesktop.basedOnMerchantSLocation')}</span>
                  </div>

                  {formTaxType === 'merchant' && (
                    <div className={styles.formGroup} style={{ marginTop: 6 }}>
                      <label className={styles.fieldLabel}>{tr('desktop.pages.billing.PlanFormDesktop.type')}</label>
                      <div className={styles.customDropdown}>
                        <div
                          ref={taxCalcTriggerRef}
                          role="button"
                          tabIndex={0}
                          className={`${styles.fieldBox} ${styles.customDropdownTrigger}`}
                          onClick={taxCalcDd.toggle}
                          onKeyDown={e => e.key === 'Enter' && taxCalcDd.toggle()}
                        >
                          <span className={styles.fieldInput}>{optLabel(TAX_CALC_OPTIONS, formTaxCalc)}</span>
                          <IonIcon icon={chevronDownOutline} className={`${styles.dropdownCaret} ${taxCalcDd.open ? styles.dropdownCaretOpen : ''}`} />
                        </div>
                        {taxCalcDd.renderList(TAX_CALC_OPTIONS, formTaxCalc, v => setFormTaxCalc(v))}
                      </div>
                    </div>
                  )}

                  <div className={styles.divider} />
                  <p className={styles.subHeading}>{tr('desktop.pages.billing.PlanFormDesktop.notificationPreferences')}</p>
                  <p className={styles.subSubtitle}>{tr('desktop.pages.billing.PlanFormDesktop.selectWhatNotificationEmailsYouWould')}</p>
                  <div className={styles.notifTable}>
                    <div className={styles.notifHeader}>
                      <span>{tr('desktop.pages.billing.PlanFormDesktop.emailType')}</span>
                      <span>{tr('desktop.pages.billing.PlanFormDesktop.merchant')}</span>
                    </div>
                    <div className={styles.notifDivider} />
                    <div className={styles.notifRow}>
                      <span>{tr('desktop.pages.billing.PlanFormDesktop.recurringQueue')}</span>
                      <IonCheckbox checked={formNotifRecurring} className={styles.checkbox} onIonChange={e => {
                        const next = e.detail.checked;
                        setFormNotifRecurring(next);
                        if (!next) setFormBusinessEmail('');
                      }} />
                    </div>
                    {formNotifRecurring && (
                      <>
                        <div className={styles.notifDivider} />
                        <div className={styles.notifRow}>
                          <input
                            type="email"
                            className={styles.notifEmailInput}
                            placeholder={tr('desktop.pages.billing.PlanFormDesktop.merchantEmailAddress')}
                            value={formBusinessEmail}
                            onChange={e => setFormBusinessEmail(e.target.value)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </IonContent>

      {pricingDialogOpen && (
        <div className={styles.pricingOverlay}>
          <div className={styles.pricingDialog}>
            <div className={styles.pricingHeader}>
              <span className={styles.pricingTitle}>{tr('desktop.pages.billing.PlanFormDesktop.updatePricing')}</span>
              <button className={styles.pricingClose} onClick={() => setPricingDialogOpen(false)}>
                <IonIcon icon={closeOutline} />
              </button>
            </div>
            <p className={styles.pricingBody}>{tr('desktop.pages.billing.PlanFormDesktop.youAreUpdatingTheBaseRate')}</p>
            <div className={styles.pricingOptions}>
              <div
                className={`${styles.pricingOption} ${pricingChoice === 'future' ? styles.pricingOptionSelected : ''}`}
                onClick={() => setPricingChoice('future')}
              >
                <IonRadio ref={pricingFutureRadioRef} value="future" className={styles.radioCircle} />
                <span>{tr('desktop.pages.billing.PlanFormDesktop.forFutureSubscriptionsOnly')}</span>
              </div>
              <div
                className={`${styles.pricingOption} ${pricingChoice === 'all' ? styles.pricingOptionSelected : ''}`}
                onClick={() => setPricingChoice('all')}
              >
                <IonRadio ref={pricingAllRadioRef} value="all" className={styles.radioCircle} />
                <span>{tr('desktop.pages.billing.PlanFormDesktop.forAllSubscriptions')}</span>
              </div>
            </div>
            <div className={styles.pricingActions}>
              <IonButton fill="clear" className={styles.pricingCancelBtn} onClick={() => setPricingDialogOpen(false)}>{tr('desktop.pages.billing.PlanFormDesktop.cancel')}</IonButton>
              <IonButton fill="solid" className={styles.primaryBtn} onClick={() => { setPricingDialogOpen(false); handleSaveForm(pricingChoice === 'all'); }}>{tr('desktop.pages.billing.PlanFormDesktop.update')}</IonButton>
            </div>
          </div>
        </div>
      )}
    </IonPage>
  );
};

export default PlanFormDesktop;
